import { useFormik } from 'formik';
import * as Yup from 'yup';
import clsx from 'clsx';
import { Row, Col } from 'react-bootstrap';

type Props = {
  submissionError: string | null;
  creating: boolean;
  onSubmit: (values: AmuraFormValues) => Promise<void>;
  initialFirstName?: string;
  initialLastName?: string;
  initialEmail?: string;
  initialPhone?: string;
};

export interface AmuraFormValues {
  firstName: string;
  lastName: string;
  mobileNumber: string;
  email: string;
  healthObjectives: string;
  age: string;
  gender: string;
  weight: string;
  height: string;
  consentToPrivacyPolicy: boolean;
  consentToThirdParty: boolean;
}

const amuraSchema = Yup.object().shape({
  firstName: Yup.string().required('First Name is required'),
  lastName: Yup.string().required('Last Name is required'),
  mobileNumber: Yup.string()
    .matches(/^\d{10}$/, 'Mobile number must be exactly 10 digits')
    .required('Mobile number is required'),
  email: Yup.string().email('Invalid email').required('Email is required'),
  healthObjectives: Yup.string(),
  age: Yup.number().min(1, 'Age must be greater than 0').nullable(),
  gender: Yup.string(),
  weight: Yup.number().min(1, 'Weight must be greater than 0').nullable(),
  height: Yup.number().min(1, 'Height must be greater than 0').nullable(),
  consentToPrivacyPolicy: Yup.bool().oneOf([true], 'You must agree to the Privacy Policy').required('Required'),
  consentToThirdParty: Yup.bool().oneOf([true], 'You must consent to third-party data forwarding').required('Required'),
});

const AmuraForm: React.FC<Props> = ({ 
  submissionError, 
  creating, 
  onSubmit, 
  initialFirstName = '', 
  initialLastName = '',
  initialEmail = '', 
  initialPhone = '' 
}) => {
  const formik = useFormik({
    initialValues: {
      firstName: initialFirstName,
      lastName: initialLastName,
      mobileNumber: initialPhone,
      email: initialEmail,
      healthObjectives: '',
      age: '',
      gender: '',
      weight: '',
      height: '',
      consentToPrivacyPolicy: false,
      consentToThirdParty: false,
    },
    validationSchema: amuraSchema,
    onSubmit: async (values, { setSubmitting }) => {
      await onSubmit(values);
      setSubmitting(false);
    },
  });

  return (
    <form onSubmit={formik.handleSubmit}>
      {submissionError && (
        <div className="alert alert-danger" role="alert">{submissionError}</div>
      )}

      <Row className="mb-7">
        <Col md={6}>
          <label className="form-label fw-bolder text-dark fs-6">First Name <span className="text-danger">*</span></label>
          <input
            type="text"
            {...formik.getFieldProps('firstName')}
            className={clsx('form-control form-control-lg form-control-solid', {
              'is-invalid': (formik.touched as any).firstName && (formik.errors as any).firstName,
            }, {
              'is-valid': (formik.touched as any).firstName && !(formik.errors as any).firstName,
            })}
          />
          {(formik.touched as any).firstName && (formik.errors as any).firstName && (
            <div className="fv-plugins-message-container"><div className="fv-help-block"><span role="alert">{(formik.errors as any).firstName}</span></div></div>
          )}
        </Col>
        <Col md={6}>
          <label className="form-label fw-bolder text-dark fs-6">Last Name <span className="text-danger">*</span></label>
          <input
            type="text"
            {...formik.getFieldProps('lastName')}
            className={clsx('form-control form-control-lg form-control-solid', {
              'is-invalid': (formik.touched as any).lastName && (formik.errors as any).lastName,
            }, {
              'is-valid': (formik.touched as any).lastName && !(formik.errors as any).lastName,
            })}
          />
          {(formik.touched as any).lastName && (formik.errors as any).lastName && (
            <div className="fv-plugins-message-container"><div className="fv-help-block"><span role="alert">{(formik.errors as any).lastName}</span></div></div>
          )}
        </Col>
      </Row>

      <Row className="mb-7">
        <Col md={6}>
          <label className="form-label fw-bolder text-dark fs-6">Mobile Number <span className="text-danger">*</span></label>
          <div className="input-group">
            <span className="input-group-text">+91</span>
            <input
              type="text"
              maxLength={10}
              {...formik.getFieldProps('mobileNumber')}
              className={clsx('form-control form-control-lg form-control-solid', {
                'is-invalid': (formik.touched as any).mobileNumber && (formik.errors as any).mobileNumber,
              }, {
                'is-valid': (formik.touched as any).mobileNumber && !(formik.errors as any).mobileNumber,
              })}
              onInput={e => {
                const input = e.currentTarget;
                input.value = input.value.replace(/\D/g, '').slice(0, 10);
                formik.setFieldValue('mobileNumber', input.value);
              }}
            />
          </div>
          {(formik.touched as any).mobileNumber && (formik.errors as any).mobileNumber && (
            <div className="fv-plugins-message-container"><div className="fv-help-block"><span role="alert">{(formik.errors as any).mobileNumber}</span></div></div>
          )}
        </Col>
        <Col md={6}>
          <label className="form-label fw-bolder text-dark fs-6">Email <span className="text-danger">*</span></label>
          <input
            type="email"
            {...formik.getFieldProps('email')}
            className={clsx('form-control form-control-lg form-control-solid', {
              'is-invalid': (formik.touched as any).email && (formik.errors as any).email,
            }, {
              'is-valid': (formik.touched as any).email && !(formik.errors as any).email,
            })}
          />
          {(formik.touched as any).email && (formik.errors as any).email && (
            <div className="fv-plugins-message-container"><div className="fv-help-block"><span role="alert">{(formik.errors as any).email}</span></div></div>
          )}
        </Col>
      </Row>

      <Row className="mb-7">
        <Col md={12}>
          <label className="form-label fw-bolder text-dark fs-6">What are your health objectives?</label>
          <textarea
            {...formik.getFieldProps('healthObjectives')}
            className={clsx('form-control form-control-lg form-control-solid', {
              'is-invalid': (formik.touched as any).healthObjectives && (formik.errors as any).healthObjectives,
            }, {
              'is-valid': (formik.touched as any).healthObjectives && !(formik.errors as any).healthObjectives,
            })}
            rows={4}
          />
          {(formik.touched as any).healthObjectives && (formik.errors as any).healthObjectives && (
            <div className="fv-plugins-message-container"><div className="fv-help-block"><span role="alert">{(formik.errors as any).healthObjectives}</span></div></div>
          )}
        </Col>
      </Row>

      <Row className="mb-7">
        <Col md={6}>
          <label className="form-label fw-bolder text-dark fs-6">Age</label>
          <input
            type="number"
            {...formik.getFieldProps('age')}
            className={clsx('form-control form-control-lg form-control-solid', {
              'is-invalid': (formik.touched as any).age && (formik.errors as any).age,
            }, {
              'is-valid': (formik.touched as any).age && !(formik.errors as any).age,
            })}
          />
          {(formik.touched as any).age && (formik.errors as any).age && (
            <div className="fv-plugins-message-container"><div className="fv-help-block"><span role="alert">{(formik.errors as any).age}</span></div></div>
          )}
        </Col>
        <Col md={6}>
          <label className="form-label fw-bolder text-dark fs-6">Gender</label>
          <select
            {...formik.getFieldProps('gender')}
            className={clsx('form-control form-control-lg form-control-solid', {
              'is-invalid': (formik.touched as any).gender && (formik.errors as any).gender,
            }, {
              'is-valid': (formik.touched as any).gender && !(formik.errors as any).gender,
            })}
          >
            <option value="">Select Gender</option>
            <option value="Male">Male</option>
            <option value="Female">Female</option>
            <option value="Other">Other</option>
          </select>
          {(formik.touched as any).gender && (formik.errors as any).gender && (
            <div className="fv-plugins-message-container"><div className="fv-help-block"><span role="alert">{(formik.errors as any).gender}</span></div></div>
          )}
        </Col>
      </Row>

      <Row className="mb-7">
        <Col md={6}>
          <label className="form-label fw-bolder text-dark fs-6">Weight (in kg)</label>
          <input
            type="number"
            {...formik.getFieldProps('weight')}
            className={clsx('form-control form-control-lg form-control-solid', {
              'is-invalid': (formik.touched as any).weight && (formik.errors as any).weight,
            }, {
              'is-valid': (formik.touched as any).weight && !(formik.errors as any).weight,
            })}
          />
          {(formik.touched as any).weight && (formik.errors as any).weight && (
            <div className="fv-plugins-message-container"><div className="fv-help-block"><span role="alert">{(formik.errors as any).weight}</span></div></div>
          )}
        </Col>
        <Col md={6}>
          <label className="form-label fw-bolder text-dark fs-6">Height (in cm)</label>
          <input
            type="number"
            {...formik.getFieldProps('height')}
            className={clsx('form-control form-control-lg form-control-solid', {
              'is-invalid': (formik.touched as any).height && (formik.errors as any).height,
            }, {
              'is-valid': (formik.touched as any).height && !(formik.errors as any).height,
            })}
          />
          {(formik.touched as any).height && (formik.errors as any).height && (
            <div className="fv-plugins-message-container"><div className="fv-help-block"><span role="alert">{(formik.errors as any).height}</span></div></div>
          )}
        </Col>
      </Row>

      {/* Privacy Policy Consent */}
      <Row className="mb-5">
        <Col md={12}>
          <div className="form-check form-check-custom form-check-solid">
            <input
              className="form-check-input"
              type="checkbox"
              id="amura_privacy_policy_agree"
              {...formik.getFieldProps('consentToPrivacyPolicy')}
            />
            <label
              className="form-check-label fw-bold text-gray-700 fs-6"
              htmlFor="amura_privacy_policy_agree"
            >
              I agree to the{' '}
              <a href="https://futurisk.in/privacy-policy" target="_blank" rel="noopener noreferrer" className="link-primary">
                Privacy Policy
              </a>
              .
            </label>
            {(formik.touched as any).consentToPrivacyPolicy && (formik.errors as any).consentToPrivacyPolicy && (
              <div className="fv-plugins-message-container"><div className="fv-help-block"><span role="alert">{(formik.errors as any).consentToPrivacyPolicy}</span></div></div>
            )}
          </div>
        </Col>
      </Row>

      {/* Third Party Data Forwarding Consent */}
      <Row className="mb-7">
        <Col md={12}>
          <div className="form-check form-check-custom form-check-solid">
            <input
              className="form-check-input"
              type="checkbox"
              id="amura_third_party_agree"
              {...formik.getFieldProps('consentToThirdParty')}
            />
            <label
              className="form-check-label fw-bold text-gray-700 fs-6"
              htmlFor="amura_third_party_agree"
            >
              I consent to my personal data being forwarded to third-party service providers for wellness services.
            </label>
            {(formik.touched as any).consentToThirdParty && (formik.errors as any).consentToThirdParty && (
              <div className="fv-plugins-message-container"><div className="fv-help-block"><span role="alert">{(formik.errors as any).consentToThirdParty}</span></div></div>
            )}
          </div>
        </Col>
      </Row>

      <div className="text-center">
        <button type="submit" className="btn btn-lg btn-primary w-100 mb-5" disabled={!formik.isValid || formik.isSubmitting}>
          Submit
        </button>
      </div>

      {creating && (
        <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center retail-overlay">
          <div className="text-center">
            <div className="spinner-border text-primary" role="status"></div>
            <div className="mt-2">Submitting...</div>
          </div>
        </div>
      )}
    </form>
  );
};

export { AmuraForm };
